import { CircleHelp } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AffiliateCustomerMetric, AffiliateCustomerMetrics } from '@/api/affiliate/types/Affiliate'
import { AppTooltip } from '@/components/AppTooltip'
import { CustomerValueIcon } from '@/components/icons/CustomerValueIcon'
import { ShoppingCartIcon } from '@/components/icons/ShoppingCartIcon'
import { SyncIcon } from '@/components/icons/SyncIcon'
import { TrendDownIcon } from '@/components/icons/TrendDownIcon'
import { TrendUpIcon } from '@/components/icons/TrendUpIcon'
import { Card, CardContent } from '@/components/ui/card'

type CustomersMetric = {
  id: string
  bgClass: string
  Icon: React.ElementType
  title: string
  tooltip: string
  value: string
  delta: string
  trend: 'up' | 'down'
}

type Props = {
  customerMetrics: AffiliateCustomerMetrics
}

export const AffiliateCustomersTab = ({ customerMetrics }: Props) => {
  const { t } = useTranslation()
  const vsLastMonth = t('affiliate.vsLastMonth')

  const metrics: CustomersMetric[] = useMemo(() => {
    const buildMetric = (metric: AffiliateCustomerMetric, formatValue: (n: number) => string, formatDelta: (n: number) => string) => {
      const diff = metric.current - metric.previous
      const sign = diff >= 0 ? '+' : '-'
      return {
        value: formatValue(metric.current),
        delta: `${sign}${formatDelta(Math.abs(diff))} ${vsLastMonth}`,
        trend: (diff >= 0 ? 'up' : 'down') as 'up' | 'down',
      }
    }

    const customerValue = buildMetric(
      customerMetrics.avgCustomerValue,
      n => `₪${Math.round(n)}`,
      n => `${Math.round(n)}`
    )
    const returningRate = buildMetric(
      customerMetrics.returningCustomersRate,
      n => `${Math.round(n)}%`,
      n => `${Math.round(n)}%`
    )
    const avgOrders = buildMetric(
      customerMetrics.avgOrdersPerCustomer,
      n => `${Math.round(n)}`,
      n => `${Math.round(n)}`
    )

    return [
      {
        id: 'customer-value',
        bgClass: 'bg-[#F2F0EC]',
        Icon: CustomerValueIcon,
        title: t('affiliate.avgCustomerValue'),
        tooltip: t('affiliate.avgCustomerValueTooltip'),
        ...customerValue,
      },
      {
        id: 'returning-customers',
        bgClass: 'bg-[#FFF7DF]',
        Icon: SyncIcon,
        title: t('affiliate.returningCustomersPercent'),
        tooltip: t('affiliate.returningCustomersPercentTooltip'),
        ...returningRate,
      },
      {
        id: 'avg-orders',
        bgClass: 'bg-[#E3F4FC]',
        Icon: ShoppingCartIcon,
        title: t('affiliate.avgOrdersPerCustomer'),
        tooltip: t('affiliate.avgOrdersPerCustomerTooltip'),
        ...avgOrders,
      },
    ]
  }, [t, vsLastMonth, customerMetrics])

  return (
    <Card className='bg-white p-4'>
      <CardContent className='space-y-4 md:px-0'>
        {metrics.map(metric => {
          const TrendIcon = metric.trend === 'up' ? TrendUpIcon : TrendDownIcon
          return (
            <div key={metric.id} className={`${metric.bgClass} text-text-secondary flex flex-col gap-2.5 rounded-lg px-3 py-4`}>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-1 text-sm'>
                  {metric.title}
                  <AppTooltip content={metric.tooltip}>
                    <CircleHelp className='size-4' />
                  </AppTooltip>
                </div>
                <metric.Icon className='size-4' />
              </div>
              <div className='text-base leading-none font-medium'>{metric.value}</div>
              <div className='flex items-center gap-1 text-sm'>
                <TrendIcon className='size-4' />
                {metric.delta}
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
